import { request } from './cloudApi';
import i18n from '../i18n';

export const adminApi = {
  // Kullanıcı işlemleri
  async getUsers({ token, userId }) {
    // SADECE SERVER'DAN ÇEK - Local database fallback'i yok
    console.log('🌐 Server\'dan kullanıcılar çekiliyor...', { token: token ? 'Token var' : 'Token yok' });
    
    try {
      const result = await request('/admin/users', {
        method: 'GET',
        token,
      });
      
      console.log('📦 Server yanıtı:', result);
      
      // Response formatını kontrol et
      if (result) {
        // Eğer result direkt users array'i ise
        if (Array.isArray(result)) {
          console.log('✅ Server\'dan kullanıcılar başarıyla alındı (array):', result.length);
          return { success: true, users: result };
        }
        
        // Eğer result.success ve result.users varsa
        if (result.success && result.users) {
          console.log('✅ Server\'dan kullanıcılar başarıyla alındı:', result.users.length);
          return result;
        }
        
        // Eğer sadece result.users varsa
        if (result.users) {
          console.log('✅ Server\'dan kullanıcılar başarıyla alındı (users field):', result.users.length);
          return { success: true, users: result.users };
        }
      }
      
      throw new Error(i18n.t('shared:adminApiErrors.invalidUsersResponse'));
    } catch (error) {
      console.error('❌ Server\'dan kullanıcılar alınamadı:', error);
      console.error('❌ Hata detayları:', {
        message: error.message,
        status: error.status,
        payload: error.payload,
        code: error.code
      });

      // Hata durumunda direkt hata fırlat - local database fallback'i yok
      const errorStatus = error.status || 500;

      // Daha açıklayıcı hata mesajı
      if (error.code === 'NETWORK_UNREACHABLE' || error.message?.includes('Sunucuya ulaşılamıyor')) {
        const urlInfo = error.url ? i18n.t('shared:adminApiErrors.serverAddress', { url: error.url }) : '';
        throw new Error(i18n.t('shared:adminApiErrors.serverUnreachable', { urlInfo }));
      }

      if (errorStatus === 401) {
        throw new Error(i18n.t('shared:adminApiErrors.sessionExpired'));
      }

      if (errorStatus === 403) {
        throw new Error(i18n.t('shared:adminApiErrors.adminRequired'));
      }

      throw error;
    }
  },

  async getUserData({ token, userId }) {
    // ÖNCE SERVER'DAN ÇEK
    try {
      return await request(`/admin/users/${userId}`, {
        method: 'GET',
        token,
      });
    } catch (error) {
      // Fallback: Local database
      if (window.api?.adminGetUser) {
        try {
          return await window.api.adminGetUser(userId);
        } catch (localError) {
          console.error('Local admin getUserData error:', localError);
        }
      }
      throw error;
    }
  },

  async impersonateUser({ token, userId }) {
    return request(`/admin/users/${userId}/impersonate`, {
      method: 'POST',
      token,
    });
  },

  async updateUser({ token, userId, userData }) {
    // ÖNCE SERVER'A GÖNDER
    try {
      return await request(`/admin/users/${userId}`, {
        method: 'PUT',
        token,
        body: userData,
      });
    } catch (error) {
      // Fallback: Local database
      if (window.api?.adminUpdateUser) {
        try {
          return await window.api.adminUpdateUser(userId, userData);
        } catch (localError) {
          console.error('Local admin updateUser error:', localError);
        }
      }
      throw error;
    }
  },

  async deleteUser({ token, userId }) {
    // ÖNCE SERVER'A GÖNDER
    try {
      return await request(`/admin/users/${userId}`, {
        method: 'DELETE',
        token,
      });
    } catch (error) {
      // Fallback: Local database
      if (window.api?.adminDeleteUser) {
        try {
          return await window.api.adminDeleteUser(userId);
        } catch (localError) {
          console.error('Local admin deleteUser error:', localError);
        }
      }
      throw error;
    }
  },

  async createUser({ token, userData }) {
    // ÖNCE SERVER'A GÖNDER
    try {
      return await request('/admin/users', {
        method: 'POST',
        token,
        body: userData,
      });
    } catch (error) {
      // Fallback: Local database
      if (window.api?.adminCreateUser) {
        try {
          return await window.api.adminCreateUser(userData);
        } catch (localError) {
          console.error('Local admin createUser error:', localError);
        }
      }
      throw error;
    }
  },

  async getUserNotes({ token, userId }) {
    return request(`/admin/users/${userId}/notes`, {
      method: 'GET',
      token,
    });
  },

  // Log işlemleri
  async getLogs({ token, filters = {} }) {
    const searchParams = new URLSearchParams();
    if (filters.type) searchParams.set('type', filters.type);
    if (filters.userId) searchParams.set('userId', filters.userId);
    if (filters.startDate) searchParams.set('startDate', filters.startDate);
    if (filters.endDate) searchParams.set('endDate', filters.endDate);
    const query = searchParams.toString();
    return request(`/admin/logs${query ? `?${query}` : ''}`, {
      method: 'GET',
      token,
    });
  },

  async exportLogs({ token, filters = {} }) {
    const searchParams = new URLSearchParams();
    if (filters.type) searchParams.set('type', filters.type);
    if (filters.userId) searchParams.set('userId', filters.userId);
    if (filters.startDate) searchParams.set('startDate', filters.startDate);
    if (filters.endDate) searchParams.set('endDate', filters.endDate);
    const query = searchParams.toString();
    return request(`/admin/logs/export${query ? `?${query}` : ''}`, {
      method: 'GET',
      token,
    });
  },

  // Rol ve yetki işlemleri
  async getRoles({ token }) {
    return request('/admin/roles', {
      method: 'GET',
      token,
    });
  },

  async updateRole({ token, userId, role, plusDurationType, plusDurationDays }) {
    // ÖNCE SERVER'A GÖNDER
    try {
      return await request(`/admin/users/${userId}/role`, {
        method: 'PUT',
        token,
        body: { role, plusDurationType, plusDurationDays },
      });
    } catch (error) {
      // Fallback: Local database
      if (window.api?.adminUpdateRole) {
        try {
          return await window.api.adminUpdateRole(userId, role);
        } catch (localError) {
          console.error('Local admin updateRole error:', localError);
        }
      }
      throw error;
    }
  },

  // Email ile kullanıcıyı admin yap (server endpoint - admin middleware olmadan)
  async makeUserAdminByEmail({ email }) {
    try {
      console.log('🌐 Server\'a admin yapma isteği gönderiliyor:', email);
      const result = await request('/admin/users/make-admin-by-email', {
        method: 'POST',
        body: { email },
      });
      
      if (result && result.success) {
        console.log('✅ Server\'dan başarılı yanıt:', result.message);
        return result;
      }
      
      throw new Error(result?.message || i18n.t('shared:adminApiErrors.adminPromoteFailed'));
    } catch (error) {
      console.error('❌ Server\'dan admin yapma hatası:', error);
      // Fallback: Local database
      if (window.api?.makeUserAdminByEmail) {
        try {
          console.log('🔄 Local database\'den admin yapılıyor...');
          return await window.api.makeUserAdminByEmail(email);
        } catch (localError) {
          console.error('❌ Local database hatası:', localError);
        }
      }
      throw error;
    }
  },

  // Sistem ayarları
  async getSystemSettings({ token }) {
    return request('/admin/settings', {
      method: 'GET',
      token,
    });
  },

  async updateSystemSettings({ token, settings }) {
    return request('/admin/settings', {
      method: 'PUT',
      token,
      body: settings,
    });
  },

  // İletişim mesajları
  async getContactMessages({ token }) {
    return request('/admin/contact-messages', {
      method: 'GET',
      token,
    });
  },

  async getContactMessage({ token, messageId }) {
    return request(`/admin/contact-messages/${messageId}`, {
      method: 'GET',
      token,
    });
  },

  async updateContactMessageStatus({ token, messageId, status }) {
    return request(`/admin/contact-messages/${messageId}/status`, {
      method: 'PUT',
      token,
      body: { status },
    });
  },

  // Moderasyon — chat message reports
  async getModerationReports({ token, status = 'all', limit, offset } = {}) {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    if (limit != null) params.set('limit', String(limit));
    if (offset != null) params.set('offset', String(offset));
    const qs = params.toString();
    return request(`/admin/moderation/reports${qs ? `?${qs}` : ''}`, {
      method: 'GET',
      token,
    });
  },

  async getModerationReport({ token, reportId }) {
    return request(`/admin/moderation/reports/${reportId}`, {
      method: 'GET',
      token,
    });
  },

  async updateModerationReport({ token, reportId, status, adminNote }) {
    return request(`/admin/moderation/reports/${reportId}`, {
      method: 'PUT',
      token,
      body: {
        ...(status != null ? { status } : {}),
        ...(adminNote !== undefined ? { adminNote } : {}),
      },
    });
  },
};

// Mock API (geliştirme için)
export const mockAdminApi = {
  async getUsers({ token }) {
    // Simüle edilmiş gecikme
    await new Promise((resolve) => setTimeout(resolve, 500));
    return {
      success: true,
      users: [
        {
          id: 'user_1',
          name: 'Ahmet Yılmaz',
          email: 'ahmet@example.com',
          role: 'user',
          status: 'active',
          createdAt: '2024-01-15T10:00:00Z',
          lastLogin: '2024-12-20T14:30:00Z',
          noteCount: 15,
        },
        {
          id: 'user_2',
          name: 'Ayşe Demir',
          email: 'ayse@example.com',
          role: 'admin',
          status: 'active',
          createdAt: '2024-02-10T09:00:00Z',
          lastLogin: '2024-12-20T16:45:00Z',
          noteCount: 32,
        },
        {
          id: 'user_3',
          name: 'Mehmet Kaya',
          email: 'mehmet@example.com',
          role: 'user',
          status: 'inactive',
          createdAt: '2024-03-20T11:00:00Z',
          lastLogin: '2024-11-15T10:20:00Z',
          noteCount: 8,
        },
      ],
    };
  },

  async getUserData({ token, userId }) {
    await new Promise((resolve) => setTimeout(resolve, 300));
    return {
      success: true,
      user: {
        id: userId,
        name: 'Ahmet Yılmaz',
        email: 'ahmet@example.com',
        role: 'user',
        status: 'active',
        createdAt: '2024-01-15T10:00:00Z',
        lastLogin: '2024-12-20T14:30:00Z',
        noteCount: 15,
      },
    };
  },

  async impersonateUser({ token, userId }) {
    await new Promise((resolve) => setTimeout(resolve, 300));
    return {
      success: true,
      impersonateToken: `impersonate_${userId}_${Date.now()}`,
      user: {
        id: userId,
        name: 'Ahmet Yılmaz',
        email: 'ahmet@example.com',
      },
    };
  },

  async updateUser({ token, userId, userData }) {
    await new Promise((resolve) => setTimeout(resolve, 400));
    return {
      success: true,
      user: {
        id: userId,
        ...userData,
      },
    };
  },

  async deleteUser({ token, userId }) {
    await new Promise((resolve) => setTimeout(resolve, 400));
    return {
      success: true,
    };
  },

  async createUser({ token, userData }) {
    await new Promise((resolve) => setTimeout(resolve, 400));
    return {
      success: true,
      user: {
        id: `user_${Date.now()}`,
        ...userData,
        createdAt: new Date().toISOString(),
        status: 'active',
        noteCount: 0,
      },
    };
  },

  async getUserNotes({ token, userId }) {
    await new Promise((resolve) => setTimeout(resolve, 300));
    return {
      success: true,
      notes: [],
    };
  },

  async getLogs({ token, filters = {} }) {
    await new Promise((resolve) => setTimeout(resolve, 400));
    return {
      success: true,
      logs: [
        {
          id: 'log_1',
          type: 'login',
          userId: 'user_1',
          userName: 'Ahmet Yılmaz',
          action: 'Giriş yapıldı',
          timestamp: '2024-12-20T14:30:00Z',
          details: { ip: '192.168.1.1' },
        },
        {
          id: 'log_2',
          type: 'note_create',
          userId: 'user_2',
          userName: 'Ayşe Demir',
          action: 'Not oluşturuldu',
          timestamp: '2024-12-20T15:00:00Z',
          details: { noteId: 'note_123' },
        },
        {
          id: 'log_3',
          type: 'admin_action',
          userId: 'user_2',
          userName: 'Ayşe Demir',
          action: 'Kullanıcı güncellendi',
          timestamp: '2024-12-20T16:00:00Z',
          details: { targetUserId: 'user_1' },
        },
      ],
    };
  },

  async exportLogs({ token, filters = {} }) {
    await new Promise((resolve) => setTimeout(resolve, 500));
    // CSV formatında mock data
    return {
      success: true,
      csv: 'ID,Type,User,Action,Timestamp\nlog_1,login,Ahmet Yılmaz,Giriş yapıldı,2024-12-20T14:30:00Z\n',
    };
  },

  async getRoles({ token }) {
    await new Promise((resolve) => setTimeout(resolve, 300));
    return {
      success: true,
      roles: [
        {
          id: 'admin',
          name: 'Admin',
          permissions: ['all'],
        },
        {
          id: 'user',
          name: 'Normal Kullanıcı',
          permissions: ['read', 'write'],
        },
        {
          id: 'readonly',
          name: 'Sadece Okuma',
          permissions: ['read'],
        },
        {
          id: 'moderator',
          name: 'Moderatör',
          permissions: ['read', 'write', 'moderate'],
        },
      ],
    };
  },

  async updateRole({ token, userId, role }) {
    await new Promise((resolve) => setTimeout(resolve, 400));
    return {
      success: true,
      user: {
        id: userId,
        role,
      },
    };
  },

  async getSystemSettings({ token }) {
    await new Promise((resolve) => setTimeout(resolve, 300));
    return {
      success: true,
      settings: {
        backup: {
          enabled: true,
          frequency: 'daily',
          retention: 30,
        },
        server: {
          url: 'https://notesplus-api.bosph.net',
          timeout: 30000,
        },
        api: {
          keys: [],
        },
        theme: {
          default: 'system',
        },
      },
    };
  },

  async updateSystemSettings({ token, settings }) {
    await new Promise((resolve) => setTimeout(resolve, 400));
    return {
      success: true,
      settings,
    };
  },
};

// Gerçek API'yi kullan (mock API kaldırıldı)
export default adminApi;

